import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MessageCircle, Send, ClipboardList } from 'lucide-react';
import { useSolicitation } from '../context/SolicitationContext';
import { useQueue } from '../context/QueueContext';
import { maskPhoneBR } from '../data/catalog';

export default function CheckoutPage() {
  const { items, clear } = useSolicitation();
  const { addCustomerRequest } = useQueue();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', whatsapp: '', email: '', city: '', notes: '' });
  const [errors, setErrors] = useState({});

  if (items.length === 0) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-16 text-center">
        <h1 className="font-serif text-3xl text-navy">Sua solicitação está vazia</h1>
        <Link to="/categoria/todos" className="mt-4 inline-block text-sm font-semibold text-navy underline" data-testid="checkout-empty-browse-link">
          Explorar tecidos
        </Link>
      </main>
    );
  }

  const set = (key) => (e) => {
    const value = key === 'whatsapp' ? maskPhoneBR(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (form.name.trim().length < 3) er.name = 'Informe seu nome completo.';
    const digits = form.whatsapp.replace(/\D/g, '');
    if (digits.length < 10) er.whatsapp = 'Informe um WhatsApp válido com DDD.';
    setErrors(er);
    if (Object.keys(er).length) return;

    const record = {
      customer: form.name.trim(),
      whatsapp: form.whatsapp,
      email: form.email,
      city: form.city,
      date: new Date().toISOString(),
      observations: form.notes,
      items: items.map((i) => ({ productSlug: i.productSlug, slug: i.productSlug, variantId: i.variantId, name: i.name, variantName: i.variantName, meters: i.meters, image: i.image, customRequest: i.customRequest || false })),
    };
    try {
      // Gravada no servidor (MongoDB) com snapshot imutável dos itens e número sequencial —
      // visível na fila de todos os vendedores, em qualquer dispositivo.
      const saved = await addCustomerRequest(record);
      clear();
      navigate('/sucesso', { state: { request: saved || record } });
    } catch {
      setErrors({ submit: 'Não foi possível enviar sua solicitação agora. Verifique sua conexão e tente novamente.' });
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-8 md:py-12" data-testid="checkout-page">
      <h1 className="font-serif text-3xl tracking-tight text-navy md:text-4xl">Finalizar Solicitação</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Revise seus tecidos e informe seus dados para nossa equipe retornar pelo WhatsApp.
      </p>

      <div className="mt-8 grid gap-8 md:grid-cols-[1fr_320px]">
        <form onSubmit={submit} className="rounded-2xl border border-navy/10 bg-white p-6" noValidate>
          <div className="space-y-5">
            <div>
              <label htmlFor="checkout-name" className="text-sm font-semibold text-navy">
                Nome <span className="text-red-600">*</span>
              </label>
              <input
                id="checkout-name"
                data-testid="checkout-name-input"
                value={form.name}
                onChange={set('name')}
                autoComplete="name"
                className="mt-1.5 h-12 w-full rounded-lg border border-input bg-white px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                aria-invalid={!!errors.name}
              />
              {errors.name && <p className="mt-1 text-xs text-red-600" role="alert" data-testid="checkout-name-error">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="checkout-whatsapp" className="text-sm font-semibold text-navy">
                WhatsApp <span className="text-red-600">*</span>
              </label>
              <input
                id="checkout-whatsapp"
                data-testid="checkout-whatsapp-input"
                value={form.whatsapp}
                onChange={set('whatsapp')}
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="(41) 99999-9999"
                className="mt-1.5 h-12 w-full rounded-lg border border-input bg-white px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                aria-describedby="whatsapp-help"
                aria-invalid={!!errors.whatsapp}
              />
              <p id="whatsapp-help" className="mt-1.5 flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
                <MessageCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#128C4B]" />
                Informe seu WhatsApp para receber a confirmação de disponibilidade, quantidades e
                valores da sua solicitação.
              </p>
              {errors.whatsapp && <p className="mt-1 text-xs text-red-600" role="alert" data-testid="checkout-whatsapp-error">{errors.whatsapp}</p>}
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="checkout-email" className="text-sm font-semibold text-navy">E-mail (opcional)</label>
                <input
                  id="checkout-email"
                  data-testid="checkout-email-input"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  autoComplete="email"
                  className="mt-1.5 h-12 w-full rounded-lg border border-input bg-white px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                />
              </div>
              <div>
                <label htmlFor="checkout-city" className="text-sm font-semibold text-navy">Cidade (opcional)</label>
                <input
                  id="checkout-city"
                  data-testid="checkout-city-input"
                  value={form.city}
                  onChange={set('city')}
                  autoComplete="address-level2"
                  className="mt-1.5 h-12 w-full rounded-lg border border-input bg-white px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                />
              </div>
            </div>
            <div>
              <label htmlFor="checkout-notes" className="text-sm font-semibold text-navy">Observações (opcional)</label>
              <textarea
                id="checkout-notes"
                data-testid="checkout-notes-input"
                value={form.notes}
                onChange={set('notes')}
                rows={3}
                placeholder="Conte para que é o projeto, prazo desejado, retirada na loja..."
                className="mt-1.5 w-full rounded-lg border border-input bg-white px-4 py-3 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
              />
            </div>
          </div>
          {errors.submit && (
            <p className="mt-5 rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm font-medium text-red-700" role="alert" data-testid="checkout-submit-error">
              {errors.submit}
            </p>
          )}
          <button
            type="submit"
            data-testid="request-checkout-submit-button"
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-hover"
          >
            <Send className="h-4 w-4" /> ENVIAR SOLICITAÇÃO
          </button>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Enviar a solicitação não representa pagamento nem confirmação automática de estoque.
          </p>
        </form>

        <aside className="h-fit rounded-2xl border border-navy/10 bg-white p-6" data-testid="checkout-summary">
          <p className="font-serif text-lg text-navy">Resumo da solicitação</p>
          <ul className="mt-4 space-y-3">
            {items.map((i) => (
              <li key={i.key} className="flex items-center gap-3 text-sm">
                {i.image ? (
                  <img src={i.image} alt={i.variantName ? `${i.name} - ${i.variantName}` : i.name} className="h-12 w-12 rounded-lg object-cover" loading="lazy" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-navy-light text-navy/40">
                    <ClipboardList className="h-5 w-5" />
                  </div>
                )}
                <div>
                  <p className="font-medium leading-tight">{i.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {i.variantName ? `${i.variantName} · ` : ''}{i.meters == null ? 'metragem a confirmar' : `${String(i.meters).replace('.', ',')} m`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </main>
  );
}
